//@flow
import React, {useState, useMemo, useEffect} from 'react';
import {LOCATION_ACTIONS} from './type';
import {fetchAdmin2, fetchAdmin3} from '../../../../../../../utils/location';
import type {ConfigurationAdmin} from '../../../../../../../utils/location/api';
import {Checkbox, makeStyles} from '@material-ui/core';
import {Add, Remove} from '@material-ui/icons';
import type {Location, DefaultSelectedAdmins} from './type';
import type {ShippingDataAdvanced} from '../../../../../../../entities';
import {
  getCheckedState,
  getCheckedStateAdmin3,
  isDisabledAdmin2,
  isDisabledAdmin3,
  isIndeterminate,
  processLocationsNodes,
} from './utils';
import {colors} from '../../../../../../../assets/styles/colors';
import {useSaveLocationsSelected} from '../../../utils';
import {useLocation} from 'react-router-dom';

type LocationItemProps = {
  location: Location,
  indexes: number[],
  country: string,
  locations: Location[],
  dispatch: Function,
  canExpand: boolean,
  adminCountryConfig: ConfigurationAdmin,
  defaultSelectedAdmins?: DefaultSelectedAdmins,
  shippingDataAdvanced: ShippingDataAdvanced,
};

const useStyles = makeStyles(theme => ({
  rowAdminHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  rowAdminHeaderText: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '10px',
  },
  buttonExpand: {
    borderRadius: '50%',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: colors.purple,
    width: '30px',
    height: '30px',
    backgroundColor: '#ebe2ff',

    '&:hover': {
      backgroundColor: '#ebe2ff',
    },
  },
  locationDisabled: {
    color: colors.grey500,
  },
}));

function LocationItem({
  location,
  indexes,
  country,
  locations,
  dispatch,
  adminCountryConfig,
  defaultSelectedAdmins,
  shippingDataAdvanced,
}: LocationItemProps) {
  const locationURL = useLocation();
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const {
    updateLocationsSelectedData,
    shippinglocationsSelected,
  } = useSaveLocationsSelected();

  const isAdmin1 = indexes.length === 1;
  const isAdmin2 = indexes.length === 2;
  // const isAdmin3 = indexes.length === 3;

  const canExpand = useMemo(() => {
    if (isAdmin1) {
      return adminCountryConfig.admin2.enabled;
    }

    if (isAdmin2) {
      return adminCountryConfig.admin3.enabled;
    }

    return false;
  }, [adminCountryConfig, isAdmin1, isAdmin2]);

  const handleToggleExpand = async () => {
    const isExpanded = location.isExpanded;
    const hasNoSubLocations = location.subLocations.length === 0;

    dispatch({type: LOCATION_ACTIONS.TOGGLE_EXPAND, indexes});

    if (!isExpanded && hasNoSubLocations) {
      setLoading(true);
      setError(null);
      try {
        if (isAdmin1) {
          const admin1 = getLocationId(indexes.slice(0, 1));
          const admin1Id = admin1.adminId;
          const admin2List = await fetchAdmin2(country, admin1Id);

          dispatch({
            type: LOCATION_ACTIONS.SET_SUB_LOCATIONS,
            indexes,
            subLocations: admin2List.map(admin => ({
              adminId: admin.admin2Id,
              adminName: admin.admin2Name,
              isExpanded: false,
              isDisabled: isDisabledAdmin2(
                String(admin1Id),
                String(admin.admin2Id),
                country,
                // $FlowFixMe
                admin,
                shippingDataAdvanced,
                {
                  isChecked: getCheckedState(
                    String(admin.admin2Id),
                    // $FlowFixMe
                    defaultSelectedAdmins?.[admin1Id]
                  ),
                  location: locationURL.pathname,
                }
              ),
              subLocations: [],
              isChecked: getCheckedState(
                String(admin.admin2Id),
                // $FlowFixMe
                defaultSelectedAdmins?.[admin1Id]
              ),

              indeterminate: isIndeterminate(
                String(admin.admin2Id),
                // $FlowFixMe
                defaultSelectedAdmins?.[admin1Id]
              ),
            })),
          });
        } else if (isAdmin2) {
          const admin1 = getLocationId(indexes.slice(0, 1));
          const admin2 = getLocationId(indexes);

          const admin1Id = admin1.adminId;
          const admin2Id = admin2.adminId;

          const admin3List = await fetchAdmin3(country, admin1Id, admin2Id);
          dispatch({
            type: LOCATION_ACTIONS.SET_SUB_LOCATIONS,
            indexes,
            subLocations: admin3List.map(admin => ({
              adminId: admin.admin3Id,
              adminName: admin.admin3Name,
              isExpanded: false,
              isDisabled: isDisabledAdmin3(
                String(admin1Id),
                String(admin2Id),
                String(admin.admin3Id),
                country,
                admin,
                shippingDataAdvanced,
                {
                  isChecked: getCheckedState(
                    String(admin.admin3Id),
                    // $FlowFixMe
                    defaultSelectedAdmins?.[admin1Id]?.[admin2Id]
                  ),
                  location: locationURL.pathname,
                }
              ),
              isChecked: defaultSelectedAdmins?.[admin1Id]?.[admin2Id]
                ? getCheckedState(
                    String(admin.admin3Id),
                    // $FlowFixMe
                    defaultSelectedAdmins?.[admin1Id]?.[admin2Id]
                  )
                : getCheckedStateAdmin3(
                    String(admin1Id),
                    String(admin.admin3Id),
                    country,
                    shippingDataAdvanced,
                    // $FlowFixMe
                    defaultSelectedAdmins?.[admin1Id]?.[admin2Id],
                    locationURL.pathname
                  ),
              indeterminate: isIndeterminate(
                String(admin.admin3Id),
                // $FlowFixMe
                defaultSelectedAdmins?.[admin1Id]?.[admin2Id]
              ),
              subLocations: [],
            })),
          });
        }
      } catch (error) {
        console.error('Failed to fetch sub locations:', error);
        setError('Failed to load locations. Please try again.');
      }
      setLoading(false);
    }
  };

  const getLocationId = (indexes: number[]): Location => {
    let loc: Location = {
      subLocations: locations,
      adminId: '',
      adminName: '',
      isChecked: false,
      isExpanded: false,
      indeterminate: false,
      isDisabled: false,
    };
    indexes.forEach(index => {
      loc = loc.subLocations[index];
      if (!loc) {
        throw new Error('Location not found');
      }
    });

    return loc;
  };

  const handleToggleCheck = () => {
    dispatch({type: LOCATION_ACTIONS.TOGGLE_CHECK, indexes});
    const locationsDefault = {...shippinglocationsSelected};
    const result = processLocations(locations, country, locationsDefault);
    updateLocationsSelectedData({locationsSelected: result});
  };

  function processLocations(locations, country, locationsDefault = {}) {
    if (!country) return;

    const locationsDefaultCopy = JSON.parse(JSON.stringify(locationsDefault));

    const result = {};
    result[country] = {};

    if (!locationsDefaultCopy[country]) {
      locationsDefaultCopy[country] = {};
    }

    result[country] = processLocationsNodes(
      locations,
      result[country],
      locationsDefaultCopy ? locationsDefaultCopy[country] : {}
    );

    return result;
  }

  useEffect(() => {
    if (defaultSelectedAdmins) {
      updateLocationsSelectedData({
        locationsSelected: {
          [country]: defaultSelectedAdmins,
        },
      });
    }
  }, [country, defaultSelectedAdmins, updateLocationsSelectedData]);

  return (
    <div style={{marginLeft: `${indexes.length * 13}px`}}>
      <div className={classes.rowAdminHeader}>
        <div className={classes.rowAdminHeaderText}>
          {canExpand && (
            <button
              onClick={handleToggleExpand}
              disabled={loading}
              className={classes.buttonExpand}
              type="button"
            >
              {loading ? '...' : location.isExpanded ? <Remove /> : <Add />}
            </button>
          )}

          <span className={location.isDisabled ? classes.locationDisabled : ''}>
            {location.adminName}
          </span>
        </div>
        <Checkbox
          color="primary"
          indeterminate={location.indeterminate}
          checked={location.isChecked}
          onChange={handleToggleCheck}
          disabled={location.isDisabled}
        />
      </div>
      {error && <div style={{color: 'red'}}>{error}</div>}
      {location.isExpanded && location.subLocations.length > 0 && (
        <div>
          {location.subLocations.map((subLocation, index) => (
            <LocationItem
              key={subLocation.adminName}
              location={subLocation}
              indexes={[...indexes, index]}
              country={country}
              locations={locations}
              dispatch={dispatch}
              adminCountryConfig={adminCountryConfig}
              defaultSelectedAdmins={defaultSelectedAdmins}
              canExpand={canExpand}
              shippingDataAdvanced={shippingDataAdvanced}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default LocationItem;
